// =============================================================================
// ui/state/preferences.tsx — loaded preferences, and the only way to change one.
// -----------------------------------------------------------------------------
// The UI half of io/sync/preferences.ts. That module made saves PATCHES so a
// screen cannot revert a field it never showed; this one makes sure the screen
// never shows a field it does not actually know.
//
// ── 🎯 THE RULE A SETTINGS SCREEN GETS WRONG ──
//
// A failed read must not render the form.
//
// Every settings screen has the same shape: load, populate the inputs, save on
// change. If the load fails and the code falls back to defaults, the form shows
// `America/New_York`, auto-import off, an empty API key — a completely plausible
// screen — and the first edit saves those alongside it. The user did not lose
// their settings to a bug they could see; they lost them to a screen that looked
// fine.
//
// So `prefs` is NULL until a read SUCCEEDS, and `phase` distinguishes:
//
//   'loading'  nothing to render yet
//   'ready'    prefs are real — either a stored row, or the DDL defaults for a
//              user who genuinely has no row
//   'failed'   we could not read. Render the error, NOT the form.
//
// ⚠ `update()` stays available even in 'failed', and that is deliberate rather
// than an oversight: a patch only carries the field being changed, so it cannot
// clobber anything it did not read. The danger was never "save while unsure" —
// it was "save a whole object assembled from guesses". Blocking updates here
// would trade a safe operation for a stuck user.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  loadPreferences, savePreferences,
  type Preferences, type PreferencesPatch,
} from '../../io/sync/preferences';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type PreferencesPhase = 'loading' | 'ready' | 'failed';

export interface PreferencesState {
  phase: PreferencesPhase;
  /** ⚠ NULL until a read succeeded. Never defaults-as-a-fallback. */
  prefs: Preferences | null;
  /** True when the user has no stored row yet — a new account, not an error. */
  isNew: boolean;
  error: string | null;
  /** Persist ONLY these fields. Applied locally first so the input responds. */
  /**
   * Apply a patch. ⚠ RESOLVES TO THE ERROR, or `null` when it landed.
   *
   * 🔴 IT USED TO RESOLVE TO `void`, AND THAT HID A REAL LOSS (found under
   * TCR-86). It catches the failure and records it in `saveError`, which is
   * right for a settings control — but a CALLER cannot read `saveError` straight
   * after its own `await`, because the value it closed over is the one from
   * before the write. So every caller that awaited this was told the write
   * succeeded. The Fix-it dialog closed on a rejected write and threw away the
   * rule the trader had just typed.
   *
   * ⚠ Returning the error is additive: `void update(…)` and a bare `await` are
   * both still correct, and the 25 existing call sites are unchanged.
   */
  update: (patch: PreferencesPatch) => Promise<string | null>;
  /** Set by a failed save, so a screen can say the change did not stick. */
  saveError: string | null;
  reload: () => void;
}

const Ctx = createContext<PreferencesState | null>(null);

export function usePreferences(): PreferencesState {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePreferences() outside <PreferencesProvider>');
  return v;
}

/**
 * The same state, or `null` when there is no provider — for a READ that must not
 * oblige its caller to mount one.
 *
 * 🔴 THE THROW ABOVE STAYS, AND THIS IS NOT A WAY AROUND IT. A screen that WRITES
 * preferences must be inside the provider, because a save sends the whole
 * `app_flags` blob and writing one built from nothing would delete every other
 * surface's settings. That mistake should fail loudly.
 *
 * ⚠ But a READER deep in a form is a different case. `useJournalHidden` is called
 * by `JournalSection`, `PlanEditor` and `RecapEditor` to decide whether to add a
 * CSS class (TCR-81). Making the throwing accessor reach that deep would oblige
 * four existing test files — 100+ assertions that have nothing to do with
 * visibility — to mount a preferences provider, and would make any future
 * isolated render of those components fail for a cosmetic reason.
 *
 * ▶ Same reasoning `useDemo` records for returning "not in a demo" rather than
 * throwing: *"which is why adding it here does not oblige every Dashboard test to
 * mount one."* In the real app the Journal tab is always inside the provider —
 * `useJournalTab` itself calls the throwing accessor — so this changes nothing
 * about production behaviour.
 */
export function usePreferencesOptional(): PreferencesState | null {
  return useContext(Ctx) ?? null;
}

export function PreferencesProvider(
  { sb, userId, children }: { sb: SupabaseClient; userId: string; children: ReactNode },
) {
  const [phase, setPhase] = useState<PreferencesPhase>('loading');
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const { prefs: p, exists } = await loadPreferences(sb, userId);
        if (cancelled) return;
        setPrefs(p);
        setIsNew(!exists);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ prefs stays NULL. See the header — this is the whole point.
        setError(reportFailure('preferences', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, userId, nonce]);

  const update = useCallback(async (patch: PreferencesPatch): Promise<string | null> => {
    setSaveError(null);
    // Optimistic locally so the control does not lag the keystroke. Merged onto
    // whatever we hold — and only ever merged, never replaced, so a concurrent
    // reload cannot drop the edit.
    setPrefs((cur) => (cur ? { ...cur, ...patch } : cur));
    try {
      await savePreferences(sb, userId, patch);
      // A row now exists whether or not one did before.
      setIsNew(false);
      return null;
    } catch (e) {
      // ⚠ The optimistic value is deliberately NOT rolled back. Reverting the
      // input under the user's cursor while they are still typing is worse than
      // showing the value they entered next to an error that says it did not
      // save — and a rollback races the next keystroke.
      const msg = reportFailure('save preferences', e);
      setSaveError(msg);
      // ⚠ RETURNED AS WELL AS RECORDED. `saveError` is for the screen; this is
      // for a caller that has to decide something — close a dialog, clear a
      // draft — and cannot read the state it closed over. See the type above.
      return msg;
    }
  }, [sb, userId]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<PreferencesState>(
    () => ({ phase, prefs, isNew, error, saveError, update, reload }),
    [phase, prefs, isNew, error, saveError, update, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
